import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { deriveProxyAddress } from '@/lib/pmProxyWallet'

export type ActiveWallet = 'pm' | null

interface PmWalletState {
  /** AES-encrypted private key (persisted in localStorage) */
  encryptedData: string | null
  /** Decrypted private key — in-memory only, never persisted */
  pmPrivateKey: string | null
  /** EOA address derived from private key */
  pmAddress: string | null
  /** POLY_PROXY address derived from EOA */
  pmProxyAddress: string | null
  activeWallet: ActiveWallet

  /** Import and encrypt a raw private key with PIN */
  importKey: (privateKey: string, pin: string) => Promise<void>
  /** Unlock: decrypt stored key with PIN */
  unlock: (pin: string) => Promise<boolean>
  /** Lock: clear in-memory key */
  lock: () => void
  /** Whether POLY_PROXY has been deployed + approvals set (persisted) */
  pmProxyDeployed: boolean
  setPmProxyDeployed: (v: boolean) => void
  /** Disconnect PM wallet entirely */
  disconnectPm: () => void
  setActiveWallet: (value: ActiveWallet) => void
}

export const usePmWalletStore = create<PmWalletState>()(
  persist(
    (set, get) => ({
      encryptedData: null,
      pmPrivateKey: null,
      pmAddress: null,
      pmProxyAddress: null,
      pmProxyDeployed: false,
      activeWallet: null,

      async importKey(privateKey, pin) {
        const { encryptPrivateKey } = await import('@/lib/pmKeyCrypto')
        const { ethers } = await import('ethers')
        const encrypted = await encryptPrivateKey(privateKey, pin)
        const wallet = new ethers.Wallet(privateKey)
        const proxyAddress = deriveProxyAddress(wallet.address)
        set({
          encryptedData: encrypted,
          pmPrivateKey: privateKey,
          pmAddress: wallet.address,
          pmProxyAddress: proxyAddress,
          activeWallet: 'pm',
        })
      },

      async unlock(pin) {
        const { encryptedData } = get()
        if (!encryptedData) return false
        try {
          const { decryptPrivateKey } = await import('@/lib/pmKeyCrypto')
          const { ethers } = await import('ethers')
          const privateKey = await decryptPrivateKey(encryptedData, pin)
          const wallet = new ethers.Wallet(privateKey)
          const proxyAddress = deriveProxyAddress(wallet.address)
          set({ pmPrivateKey: privateKey, pmAddress: wallet.address, pmProxyAddress: proxyAddress })
          return true
        } catch {
          return false
        }
      },

      lock() {
        set({ pmPrivateKey: null, pmAddress: null, pmProxyAddress: null })
      },

      disconnectPm() {
        set({
          encryptedData: null,
          pmPrivateKey: null,
          pmAddress: null,
          pmProxyAddress: null,
          activeWallet: null,
        })
      },

      setPmProxyDeployed(v) {
        set({ pmProxyDeployed: v })
      },

      setActiveWallet(value) {
        set({ activeWallet: value })
      },
    }),
    {
      name: 'polystrike-pm',
      partialize: (s) => ({
        encryptedData: s.encryptedData,
        pmProxyDeployed: s.pmProxyDeployed,
        activeWallet: s.activeWallet,
      }),
    },
  ),
)
